import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Transaction } from '@mysten/sui/transactions';
import { toBase64 } from '@mysten/sui/utils';
import { useSuiClient } from '@mysten/dapp-kit';

import { subscribeAuthTokenRefresh } from '@/hooks/auth/utils/auth-token-registry';
import { useLoginModalStore } from '@/stores/login-modal-store';

import { useProfile } from './useProfile';
import { usePendingBets } from './usePendingBets';
import { useCurrentConnection, useEnokiSessionExpiry, useWalletTransaction, useDisconnectWallets, useAuthToken } from './auth';
import type { WalletSliceKey } from './auth/types/wallet-slice.type';
import { cancelIdleCallback, scheduleIdleCallback } from '@/utils/idle';

export type { AuthType } from './auth/types/auth-type.type';

export function useAuthLogic() {
	const client = useSuiClient();
	const [isBalanceReady, setBalanceReady] = useState(false);
	const { disconnectAll, isPending: isWalletDisconnecting, isSuccess: isWalletDisconnected } = useDisconnectWallets();
	const openLoginModal = useLoginModalStore((state) => state.openLoginModal);
	const { wallet: primaryWallet, account: primaryAccount, authType, walletProvider } = useCurrentConnection();
	const signatureRefreshPendingRef = useRef(false);
	const signatureRefreshProcessingRef = useRef(false);
	const address = primaryAccount?.address ?? null;

	const { jwtToken, requestLoginSignature, requestSignedAuthToken, issueAuthToken, ensureAuthToken, refreshAuthToken, syncAuthToken } =
		useAuthToken();
	const isZkLoginProcessing = authType === 'zklogin' && !jwtToken;

	const { profile, refreshProfile, clearProfile } = useProfile();
	const {
		balance,
		displayedBalance,
		availableBalance,
		addPendingBet,
		setPendingBetPayout,
		setPendingBetGas,
		resolvePendingBet,
		resetPendingBetsState,
	} = usePendingBets({ enabled: isBalanceReady });

	useEffect(() => {
		const handle = scheduleIdleCallback(() => setBalanceReady(true), 1500);
		return () => cancelIdleCallback(handle);
	}, []);

	const disconnect = useCallback(async () => {
		try {
			await disconnectAll();
		} catch (error) {
			console.error('Failed to disconnect wallets', error);
		}

		clearProfile();
		resetPendingBetsState();
	}, [clearProfile, disconnectAll, resetPendingBetsState]);

	useEffect(() => {
		const unsubscribe = subscribeAuthTokenRefresh(() => {
			signatureRefreshPendingRef.current = true;

			if (signatureRefreshProcessingRef.current) {
				return;
			}

			const processQueue = async () => {
				signatureRefreshProcessingRef.current = true;
				try {
					while (signatureRefreshPendingRef.current) {
						signatureRefreshPendingRef.current = false;
						try {
							await issueAuthToken();
						} catch (error) {
							console.error('Automatic authentication refresh failed:', error);
						}
					}
				} finally {
					signatureRefreshProcessingRef.current = false;
				}
			};

			void processQueue();
		});

		return unsubscribe;
	}, [issueAuthToken]);

	const executeSignedTransaction = useCallback(
		async ({ bytes, signature }: { bytes: string; signature: string }) =>
			client.executeTransactionBlock({
				transactionBlock: bytes,
				signature,
				options: {
					showRawEffects: true,
					showInput: true,
					showRawInput: false,
					showEffects: true,
					showEvents: true,
					showObjectChanges: false,
					showBalanceChanges: false,
				},
			}),
		[client],
	);

	const {
		signTransaction: walletSignTransaction,
		signAndExecuteTransaction: walletSignAndExecuteTransaction,
		executeTransaction,
		signPersonalMessage,
		verifyPersonalMessageSignature,
	} = useWalletTransaction({
		execute: executeSignedTransaction as any,
	});

	const signTransaction = useCallback(
		async (txb: Transaction, walletType?: WalletSliceKey) => {
			const { bytes, signature, reportTransactionEffects } = await walletSignTransaction(txb, walletType);

			return {
				bytes,
				signature,
				reportTransactionEffects: reportTransactionEffects
					? (effects?: string | number[]) => {
							if (!effects) {
								return;
							}
							const normalized = Array.isArray(effects) ? toBase64(new Uint8Array(effects)) : effects;
							reportTransactionEffects(normalized);
						}
					: undefined,
			};
		},
		[walletSignTransaction],
	);

	const signAndExecuteTransaction = useCallback(
		async (txb: Transaction, walletType?: WalletSliceKey) => walletSignAndExecuteTransaction(txb, walletType),
		[walletSignAndExecuteTransaction],
	);

	const handleSessionExpire = useCallback(async () => {
		if (authType !== 'zklogin') {
			return;
		}

		try {
			await disconnect();
		} catch (error) {
			console.error('Failed to disconnect expired Enoki session', error);
		} finally {
			clearProfile();
			resetPendingBetsState();
			openLoginModal({ sessionExpired: true });
		}
	}, [authType, clearProfile, disconnect, openLoginModal, resetPendingBetsState]);

	const sessionExpired = useEnokiSessionExpiry({ onExpire: handleSessionExpire });

	return {
		authType,
		isZkLoginProcessing,
		isLogged: !!primaryAccount,
		isWalletDisconnecting,
		isWalletDisconnected,
		sessionExpired,
		address,
		currentAccount: primaryAccount,
		accounts: primaryWallet?.accounts,
		primaryWallet,
		primaryAccount,
		walletProvider: walletProvider,
		profile,
		refreshProfile,
		balance,
		displayedBalance,
		availableBalance,
		disconnect,
		signAndExecuteTransaction,
		signTransaction,
		executeTransaction,
		addPendingBet,
		setPendingBetPayout,
		setPendingBetGas,
		resolvePendingBet,
		signPersonalMessage,
		requestLoginSignature,
		requestSignedAuthToken,
		jwtToken,
		syncAuthToken,
		ensureAuthToken,
		refreshAuthToken,
		verifyPersonalMessageSignature,
	};
}

export const AuthContext = createContext<ReturnType<typeof useAuthLogic> | null>(null);

export function useAuth() {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}
